import { inject, Injectable } from '@angular/core';
import { DocumentApi, DocumentEntity } from '@domain';
import {
    DialogConfirmationControl,
    RaDialogConfirmationService,
    RaToastService,
} from '@shared/components';
import { catchError, EMPTY, map, Observable, switchMap } from 'rxjs';
import { UtilService } from 'src/app/shared/utils/utils.service';
import { DomainTranslateService } from '../../utils/domain-translate.service';

@Injectable({
    providedIn: 'root',
})
export class DocumentSectionController {
    // * [API]
    private _api = inject(DocumentApi);

    // * [Services]
    private _utilService = inject(UtilService);
    private _raToastService = inject(RaToastService);
    private _raDialogConfirmationService = inject(RaDialogConfirmationService);
    private _i18n = inject(DomainTranslateService);

    /**
     * Create a session
     * @param documentId ID of document
     * @returns Observable of ResponseApiDto<DocumentEntity>
     */
    public createSection(documentId: string): Observable<DocumentEntity> {
        return this._api.createSession(documentId).pipe(
            map(response => response.body),
            catchError(error =>
                this._utilService.handleError(
                    error,
                    this._i18n.instant('domain.document.errors.createSession')
                )
            )
        );
    }

    /**
     * Remove sessions
     * @param documentId ID of document
     * @param sessionIds IDs of sessions
     * @returns Observable of ResponseApiDto<DocumentEntity>
     */
    public removeSections(documentId: string, sessionIds: string[]): Observable<DocumentEntity> {
        return this._api.removeSessions(documentId, sessionIds).pipe(
            map(response => {
                // Mensagem de sucesso
                this._raToastService.success({
                    message:
                        response.message[0] ||
                        this._i18n.instant('domain.document.success.sessionsRemoved'),
                });

                return response.body;
            }),
            catchError(error =>
                this._utilService.handleError(
                    error,
                    this._i18n.instant('domain.document.errors.removeSessions')
                )
            )
        );
    }

    /**
     * Remove sessions
     * @param documentId ID of document
     * @param sessionIds IDs of sessions
     * @returns Observable of ResponseApiDto<DocumentEntity>
     */
    public deleteSection(documentId: string, sessionId: string): Observable<void> {
        return this._raDialogConfirmationService
            .confirm({
                title: this._i18n.instant('domain.document.dialog.deleteSession.title'),
                message: this._i18n.instant('domain.document.dialog.deleteSession.message'),
                confirmText: this._i18n.instant('common.delete'),
                cancelText: this._i18n.instant('common.cancel'),
                type: 'danger',
                irreversible: true,
            })
            .pipe(
                switchMap((dialogControl: DialogConfirmationControl) => {
                    // Ativar loading
                    dialogControl.setLoading(true);

                    return this._api.removeSessions(documentId, [sessionId]).pipe(
                        map(() => {
                            dialogControl.close();
                            return void 0;
                        }),
                        catchError(error => {
                            dialogControl.setLoading(false);
                            this._utilService.handleError(
                                error,
                                this._i18n.instant('domain.document.errors.deleteSession')
                            );

                            return EMPTY;
                        })
                    );
                }),
                map(() => {
                    // Mensagem de sucesso
                    this._raToastService.success({
                        message: this._i18n.instant('domain.document.success.sessionDeleted'),
                    });

                    return void 0;
                })
            );
    }

    /**
     * Reorder sessions
     * @param documentId ID of document
     * @param sourceIndex Source index of session
     * @param targetIndex Target index of session
     * @returns Observable of void
     */
    public reorderSections(
        documentId: string,
        sourceIndex: number,
        targetIndex: number
    ): Observable<void> {
        return this._api.reorderSessions(documentId, sourceIndex, targetIndex).pipe(
            map(() => void 0),
            catchError(error =>
                this._utilService.handleError(
                    error,
                    this._i18n.instant('domain.document.errors.reorderSessions')
                )
            )
        );
    }

    /**
     * Set focus in session
     * @param documentId ID of document
     * @param sessionIds IDs of sessions
     * @returns Observable of ResponseApiDto<DocumentEntity>
     */
    public setFocusInSection(documentId: string, sessionId: string): Observable<void> {
        return this._api.setFocusInSession(documentId, sessionId).pipe(
            map(() => void 0),
            catchError(error =>
                this._utilService.handleError(
                    error,
                    this._i18n.instant('domain.document.errors.setFocus')
                )
            )
        );
    }

    /**
     * Refresh focus in session
     * @param documentId ID of document
     * @param sessionId ID of session
     * @returns Observable of ResponseApiDto<DocumentEntity>
     */
    public refreshFocusInSection(documentId: string, sessionId: string): Observable<void> {
        return this._api.refreshFocusInSession(documentId, sessionId).pipe(
            map(() => void 0),
            catchError(error =>
                this._utilService.handleError(
                    error,
                    this._i18n.instant('domain.document.errors.refreshFocus')
                )
            )
        );
    }

    /**
     * Remove focus in session
     * @param documentId ID of document
     * @returns Observable of ResponseApiDto<DocumentEntity>
     */
    public removeFocusInSection(documentId: string): Observable<void> {
        return this._api.removeFocusInSession(documentId).pipe(
            map(() => void 0),
            catchError(error =>
                this._utilService.handleError(
                    error,
                    this._i18n.instant('domain.document.errors.removeFocus')
                )
            )
        );
    }

    /**
     * Update name of session
     * @param documentId ID of document
     * @param sessionId ID of session
     * @param name Name of session
     * @returns Observable of ResponseApiDto<DocumentEntity>
     */
    public updateSectionName(
        documentId: string,
        sessionId: string,
        name: string
    ): Observable<DocumentEntity> {
        return this._api.updateSessionName(documentId, sessionId, name).pipe(
            map(response => {
                // Mensagem de sucesso
                this._raToastService.success({
                    message:
                        response.message[0] ||
                        this._i18n.instant('domain.document.success.sessionRenamed'),
                });

                return response.body;
            }),
            catchError(error =>
                this._utilService.handleError(
                    error,
                    this._i18n.instant('domain.document.errors.updateSessionName')
                )
            )
        );
    }

    /**
     * Atualiza a descrição de uma seção. Sem toast (auto-save discreto).
     */
    public updateSectionDescription(
        documentId: string,
        sessionId: string,
        description: string
    ): Observable<DocumentEntity> {
        return this._api.updateSessionDescription(documentId, sessionId, description).pipe(
            map(response => response.body),
            catchError(error =>
                this._utilService.handleError(
                    error,
                    this._i18n.instant('domain.document.errors.updateSessionDescription')
                )
            )
        );
    }

    /**
     * Marca/desmarca uma seção como concluída (só responsáveis). Sem toast — o estado
     * é refletido na própria UI (checkbox + progresso).
     */
    public setSessionCompleted(
        documentId: string,
        sessionId: string,
        done: boolean,
        itemId?: string
    ): Observable<DocumentEntity> {
        return this._api.setSessionCompleted(documentId, sessionId, done, itemId).pipe(
            map(response => response.body),
            catchError(error =>
                this._utilService.handleError(
                    error,
                    this._i18n.instant('domain.document.errors.setSessionCompleted')
                )
            )
        );
    }

    /**
     * Renomeia um campo (usado para a pasta de checklist). Sem toast — reflete na UI.
     */
    public updateFieldName(
        documentId: string,
        sessionId: string,
        fieldId: string,
        name: string
    ): Observable<DocumentEntity> {
        return this._api.updateFieldName(documentId, sessionId, fieldId, name).pipe(
            map(response => response.body),
            catchError(error =>
                this._utilService.handleError(
                    error,
                    this._i18n.instant('domain.document.errors.updateFieldName')
                )
            )
        );
    }

    /**
     * Reseta o checklist do documento: desmarca todas as seções (só responsáveis).
     */
    public resetChecklist(documentId: string): Observable<DocumentEntity> {
        return this._api.resetChecklist(documentId).pipe(
            map(response => {
                this._raToastService.success({
                    message:
                        response.message[0] ||
                        this._i18n.instant('domain.document.success.checklistReset'),
                });

                return response.body;
            }),
            catchError(error =>
                this._utilService.handleError(
                    error,
                    this._i18n.instant('domain.document.errors.resetChecklist')
                )
            )
        );
    }
}
